/**
 * Asset types supported by the formatting system
 */
export enum AssetType {
  CRYPTO = 'crypto',
  // MEME_COIN = 'meme_coin',
  STOCK = 'stock',
  USD = 'usd',
}

/**
 * Compact notation suffixes for large numbers
 */
export enum CompactNotation {
  K = 'K',
  M = 'M',
  B = 'B',
  T = 'T',
}

/**
 * Configuration for formatting a specific value
 */
export interface FormatOptions {
  /**
   * Type of asset being formatted
   */
  assetType?: AssetType;

  /**
   * Symbol or ticker (e.g., 'BTC', 'PEPE', 'AAPL')
   */
  symbol?: string;

  /**
   * Whether to show compact notation (K, M, B, T)
   */
  compact?: boolean;

  /**
   * Whether to show currency symbol
   */
  showSymbol?: boolean;

  /**
   * Currency symbol to display
   */
  currencySymbol?: string;

  /**
   * Custom decimal places (overrides default)
   */
  decimals?: number;

  /**
   * Force exact decimal places (don't remove trailing zeros)
   */
  forceDecimals?: boolean;

  /**
   * Include sign for positive numbers (+)
   */
  showSign?: boolean;

  /**
   * Thousands separator
   */
  separator?: boolean;
}

/**
 * Result of formatting operations
 */
export interface FormattedPrice {
  /**
   * The formatted string ready for display
   */
  display: string;

  /**
   * The original value as a Decimal
   */
  original: string;

  /**
   * The decimal precision used
   */
  decimals: number;

  /**
   * Whether the value was truncated
   */
  isTruncated: boolean;

  /**
   * The actual number used for display (before formatting)
   */
  value: string;

  /**
   * Compact notation if applicable (K, M, B, T)
   */
  compactSuffix?: string;
}

/**
 * Asset precision configuration
 */
export interface AssetPrecisionConfig {
  /**
   * Asset type
   */
  assetType: AssetType;

  /**
   * Default decimal places for display
   */
  displayDecimals: number;

  /**
   * Exact decimal places for internal calculations
   */
  calculationDecimals: number;

  /**
   * Whether fractional units are allowed
   */
  isFractional: boolean;

  /**
   * Minimum displayable value
   */
  minDisplay?: string;

  /**
   * Patterns for symbols (e.g., meme coins)
   */
  symbolPatterns?: string[];
}

/**
 * Configuration for balance calculations
 */
export interface BalanceCalculationConfig {
  /**
   * Available balance
   */
  available: string;

  /**
   * Reserved/locked balance
   */
  reserved?: string;

  /**
   * Total balance
   */
  total: string;

  /**
   * Pending orders total
   */
  pending?: string;

  /**
   * Currency type
   */
  currency?: string;

  /**
   * Tolerance percentage for rounding (default: 0.01%)
   */
  tolerance?: number;
}

/**
 * Result of max trade calculation
 */
export interface MaxTradeResult {
  /**
   * Maximum quantity that can be traded
   */
  maxQuantity: string;

  /**
   * Maximum amount that can be spent/received
   */
  maxAmount: string;

  /**
   * Reason for limitation (if any)
   */
  limitReason?: string;

  /**
   * Whether balance is insufficient
   */
  isInsufficientBalance: boolean;

  /**
   * Fee consideration
   */
  includesFee: boolean;
}

/**
 * Precision tolerance configuration
 */
export interface PrecisionTolerance {
  /**
   * Epsilon value for comparison
   */
  epsilon: number;

  /**
   * Decimal places for comparison
   */
  decimals: number;

  /**
   * Whether to use strict comparison
   */
  strict: boolean;
}

/**
 * Price history for trend calculation
 */
export interface PriceHistory {
  /**
   * Current price
   */
  current: string;

  /**
   * Previous price (for change calculation)
   */
  previous: string;

  /**
   * Percentage change
   */
  percentChange?: string;

  /**
   * Absolute change
   */
  absoluteChange?: string;
}

/**
 * Currency information
 */
export interface CurrencyInfo {
  /**
   * Currency code (USD, EUR, INR, etc.)
   */
  code: string;

  /**
   * Currency symbol
   */
  symbol: string;

  /**
   * Display name
   */
  name: string;

  /**
   * Decimal places for currency
   */
  decimals: number;

  /**
   * Is fiat currency
   */
  isFiat: boolean;
}
